<template>
  <div class="page-container">
    <v-container fluid>
      <!-- Header -->
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64" 
        type="table-heading"
      >
        <v-toolbar :image="SubNavbarBackGround" dark class="header-title">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Generate Question Set</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- Main Content Card -->
      <v-card class="main-form-card" elevation="0">
        <v-card-text class="pa-8">
          <!-- Page Title -->
          <div class="page-header">
            <div class="d-flex align-center gap-3 mb-2">
                <v-icon style="color: #000000BB !important; margin-right: 25px;" size="40">mdi-head-question-outline</v-icon>
              <div>
                <h1 class="page-title">Generate Question Set</h1>
                <p class="page-subtitle">Create intelligent questions from your knowledge documents</p>
              </div>
            </div>
          </div>

          <!-- Form -->
          <v-form ref="form" v-model="valid" lazy-validation>
            <div class="form-layout">
              <!-- Left Column -->
              <div class="form-left-panel">
                <!-- Question Set Name -->
                <div class="form-section">
                  <v-text-field 
                    id="questionsetname-field-validationgenerateupload" 
                    label="Question Set Name" 
                    v-model="Validation.validationName"
                    :rules="[rules.required]" 
                    required 
                    variant="outlined"
                    density="comfortable"
                    class="modern-input"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon 
                            size="small" 
                            color="primary"
                            v-bind="props"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>{{ $t('_InfoValidationName') }}</span>
                      </v-tooltip> 
                    </template>
                  </v-text-field>
                </div>

                <!-- Configuration Sections -->
                <v-expansion-panels 
                  multiple 
                  flat 
                  v-model="panel" 
                  class="modern-expansion-panels"
                  variant="accordion"
                >
                  <!-- Knowledge Documents Section -->
                  <v-expansion-panel class="expansion-panel-modern">
                    <v-expansion-panel-title class="panel-header">
                      <div class="d-flex align-center gap-2">
                        <v-icon color="primary" size="20">mdi-book-open-variant</v-icon>
                        <span class="panel-title">Knowledge Documents</span>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="panel-content">
                      <div class="form-grid">
                        <v-autocomplete 
                          id="documentname-autocomplete-validationgenerateupload" 
                          label="Document Name" 
                          :items="DDVector" 
                          item-title="name"
                          item-value="name" 
                          chips 
                          closable-chips 
                          multiple 
                          clearable 
                          variant="outlined" 
                          density="comfortable"
                          v-model="Validation.vectordbName"
                          :rules="[rules.required]" 
                          required
                          class="modern-autocomplete"
                          @update:model-value="validate"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoVectorDBName') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-autocomplete>

                        <v-text-field 
                          id="questionsperchunk-field-validationgenerateupload" 
                          label="Questions per Chunk" 
                          @keypress="isNumberInt($event)" 
                          clearable 
                          density="comfortable"
                          variant="outlined" 
                          v-model="Validation.amountQuestions" 
                          :rules="[rules.required]"
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoNumberOfQuestions') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <!-- Parameters Section -->
                  <v-expansion-panel class="expansion-panel-modern">
                    <v-expansion-panel-title class="panel-header">
                      <div class="d-flex align-center gap-2">
                        <v-icon color="primary" size="20">mdi-tune</v-icon>
                        <span class="panel-title">Parameters</span>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="panel-content">
                      <div class="form-grid-2">
                        <v-text-field 
                          label="Temperature*" 
                          v-model="Validation.temperature"
                          @keypress="isNumber($event)" 
                          variant="outlined"
                          density="comfortable"
                          :rules="[validateInput0_1(Validation.temperature)]" 
                          required 
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top" max-width="300px">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoTemperature') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>

                        <v-text-field 
                          label="Top P" 
                          v-model="Validation.topP" 
                          @keypress="isNumber($event)"
                          variant="outlined" 
                          density="comfortable"
                          :rules="[validateInput0_1(Validation.topP)]" 
                          required 
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top" max-width="300px">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoTopP') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>

                        <v-text-field 
                          label="Frequency Penalty" 
                          v-model="Validation.frequencyPenalty"
                          @keypress="isNumber($event)" 
                          variant="outlined" 
                          density="comfortable"
                          :rules="[validateInput0_2(Validation.frequencyPenalty)]"
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top" max-width="300px">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoFrequencyPenalty') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>

                        <v-text-field 
                          label="Presence Penalty" 
                          v-model="Validation.presencePenalty"
                          @keypress="isNumber($event)" 
                          variant="outlined" 
                          density="comfortable"
                          :rules="[validateInput0_2(Validation.presencePenalty)]"
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoPresencePenalty') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>

                        <v-autocomplete 
                          label="Max Tokens Response" 
                          :items="[2000, 3000]" 
                          clearable 
                          variant="outlined" 
                          density="comfortable"
                          v-model="Validation.maxTokens"
                          class="modern-autocomplete"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoMaxToken') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-autocomplete>

                        <v-text-field 
                          label="Stop Sequence" 
                          v-model="Validation.stop" 
                          variant="outlined" 
                          density="comfortable"
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoStop') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <!-- Tone Section -->
                  <v-expansion-panel class="expansion-panel-modern">
                    <v-expansion-panel-title class="panel-header">
                      <div class="d-flex align-center gap-3">
                        <v-icon color="primary" size="20">mdi-microphone-variant</v-icon>
                        <span class="panel-title">Tone</span>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="panel-content">
                      <div class="form-grid mb-6">
                        <v-text-field  
                          id="questionspertone-field-validationgenerateupload" 
                          label="Questions per Tone" 
                          @keypress="isNumberInt($event)" 
                          clearable 
                          density="comfortable"
                          variant="outlined" 
                          v-model="Validation.amountQuestionsWithTone" 
                          :disabled="chkTone()"
                          :rules="[rules.required]"
                          class="modern-input"
                        >
                          <template v-slot:append-inner>
                            <v-tooltip location="top">
                              <template v-slot:activator="{ props }">
                                <v-icon 
                                  size="small" 
                                  color="primary"
                                  v-bind="props"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_InfoNumberOfQuestionsTone') }}</span>
                            </v-tooltip> 
                          </template>
                        </v-text-field>
                      </div>

                      <!-- Tone Options Grid -->
                      <div class="tone-grid">
                        <!-- Firm -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.firm }"
                          @click="toggleTone('firm')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="firm-checkbox-validationgenerateupload" 
                                v-model="Validation.firm" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.firm, 'Firm')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Firm</h4>
                                <p class="tone-description">Generate questions with decisive and assertive language that demands clear, definitive answers</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Confident -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.confident }"
                          @click="toggleTone('confident')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="confident-checkbox-validationgenerateupload" 
                                v-model="Validation.confident" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.confident, 'Confident')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Confident</h4>
                                <p class="tone-description">Create questions with strong conviction and certainty that inspire authoritative responses</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Poetic -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.poetic }"
                          @click="toggleTone('poetic')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="poetic-checkbox-validationgenerateupload" 
                                v-model="Validation.poetic" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.poetic, 'Poetic')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Poetic</h4>
                                <p class="tone-description">Craft questions with artistic and expressive language that evokes creativity and imagination</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Narrative -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.narrative }"
                          @click="toggleTone('narrative')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="narrative-checkbox-validationgenerateupload" 
                                v-model="Validation.narrative" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.narrative, 'Narrative')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Narrative</h4>
                                <p class="tone-description">Design questions that encourage storytelling and detailed explanations with context</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Professional -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.professional }"
                          @click="toggleTone('professional')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="professional-checkbox-validationgenerateupload" 
                                v-model="Validation.professional" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.professional, 'Professional')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Professional</h4>
                                <p class="tone-description">Generate questions using business-appropriate language suitable for workplace communications</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Descriptive -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.descriptive }"
                          @click="toggleTone('descriptive')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="descriptive-checkbox-validationgenerateupload" 
                                v-model="Validation.descriptive" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.descriptive, 'Descriptive')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Descriptive</h4>
                                <p class="tone-description">Create questions that prompt detailed, vivid explanations and comprehensive responses</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Humorous -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.humorous }"
                          @click="toggleTone('humorous')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="humorous-checkbox-validationgenerateupload" 
                                v-model="Validation.humorous" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.humorous, 'Humorous')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Humorous</h4>
                                <p class="tone-description">Formulate questions with light-hearted and entertaining elements that engage users playfully</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Academic -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.academic }"
                          @click="toggleTone('academic')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="academic-checkbox-validationgenerateupload" 
                                v-model="Validation.academic" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.academic, 'Academic')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Academic</h4>
                                <p class="tone-description">Develop questions using scholarly language appropriate for educational and research contexts</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Persuasive -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.persausive }"
                          @click="toggleTone('persausive')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="persausive-checkbox-validationgenerateupload" 
                                v-model="Validation.persausive" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.persausive, 'Persausive')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Persuasive</h4>
                                <p class="tone-description">Generate questions that encourage influential and convincing responses with strong arguments</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Formal -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.format }"
                          @click="toggleTone('format')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="format-checkbox-validationgenerateupload" 
                                v-model="Validation.format" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.format, 'Format')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Formal</h4>
                                <p class="tone-description">Create questions using proper, structured language suitable for official communications</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Informal -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.informal }"
                          @click="toggleTone('informal')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="informal-checkbox-validationgenerateupload" 
                                v-model="Validation.informal" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.informal, 'Informal')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Informal</h4>
                                <p class="tone-description">Formulate questions using casual, conversational language for relaxed interactions</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Friendly -->
                        <v-card 
                          class="tone-option-card"
                          :class="{ 'selected': Validation.friendly }"
                          @click="toggleTone('friendly')"
                        >
                          <v-card-text class="tone-card-content">
                            <div class="d-flex align-start">
                              <v-checkbox 
                                id="friendly-checkbox-validationgenerateupload" 
                                v-model="Validation.friendly" 
                                color="primary" 
                                density="comfortable"
                                @change="InsertTextToPrompt(Validation.friendly, 'Friendly')" 
                                hide-details
                                class="tone-checkbox-minimal"
                                @click.stop
                              />
                              <div class="tone-content ml-3">
                                <h4 class="tone-title">Friendly</h4>
                                <p class="tone-description">Design questions with warm, approachable language that creates comfortable dialogue</p>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>

              <!-- Right Column - Prompt Areas -->
              <div class="form-right-panel">
                <div class="prompt-section">
                  <h3 class="section-title">
                    <v-icon color="primary" class="mr-2">mdi-pencil-plus</v-icon>
                    Prompt Configuration
                  </h3>
                  
                  <v-textarea 
                    label="Add more prompts" 
                    variant="outlined" 
                    counter 
                    auto-grow 
                    rows="8"
                    v-model="Validation.promptMore"
                    class="modern-textarea mb-4"
                    density="comfortable"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip location="top" max-width="300px">
                        <template v-slot:activator="{ props }">
                          <v-icon 
                            size="small" 
                            color="primary"
                            v-bind="props"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>{{ $t('_InfoTemperature') }}</span>
                      </v-tooltip> 
                    </template>
                  </v-textarea>
                  
                  <v-textarea 
                    label="Prompt template" 
                    variant="outlined" 
                    counter 
                    auto-grow 
                    rows="12"
                    v-model="Validation.promptTemplate" 
                    :rules="[rules.required]" 
                    required 
                    readonly
                    class="modern-textarea readonly-textarea"
                    density="comfortable"
                  >
                    <template v-slot:append-inner>
                      <div class="d-flex align-center gap-1">                        
                        <v-tooltip location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon
                              size="small"
                              variant="text"
                              :color="copyButtonState.copied ? 'success' : 'primary'"
                              @click="copyToClipboard(Validation.promptTemplate)"
                              class="copy-btn"
                              :disabled="copyButtonState.copying"
                            >
                              <v-icon size="small">
                                {{ copyButtonState.copied ? 'mdi-check' : 'mdi-content-copy' }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ copyButtonState.copied ? 'Copied!' : 'Copy prompt template' }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-textarea>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions">
              <v-btn 
                variant="outlined"
                color="orange"
                size="large"
                to="/ValidationGenerate"
                class="back-button mr-4"
                prepend-icon="mdi-arrow-left"
                style="width: 120px;"
              >
                Back
              </v-btn>
              <v-btn
                v-if="!isViewOnly"
                id="button-process-validationgenerateupload"
                variant="flat"
                color="primary"
                size="large"
                @click="Save()"
                class="create-btn"
                prepend-icon="mdi-auto-fix"
                style="width: 150px;"
                :disabled="Validation.temperature === '' || Validation.topP === '' "
              >
                Process
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Loading Dialog -->
    <v-overlay :model-value="dialog" class="align-center justify-center">
      <v-card class="loading-card pa-8" min-width="300">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            class="mb-4"
          ></v-progress-circular>
          <p class="loading-text mb-0">Loading Please Wait...</p>
        </div>
      </v-card>
    </v-overlay>
  </div>
</template>
<script>
// import DragDropFile from "@/components/DragDropFile.vue";
export default {
  name: 'ValidationGenerateUpload',
  inject: ['flagNav','FontColorTitle','SubNavbarBackGround'],
  // components: { DragDropFile },
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('ValidationGenerateUpload');
    },
  },
  data: () => ({
    props: ['id'],
    id: null,
    valid: true,
    checkbox1: false,
    editAction: false,
    dialog: false,
    tempID: 0,
    MenuID1: null,
    MenuID2: null,
    MenuID3: null,
    menuID: null,
    subMenuID: null,
    today: null,
    maxLengthChar: 0,
    loading: true,
    panel: [0],
    DDVector: [],
    copyButtonState: {
      copied: false,
      copying: false
    },
    rules: {
      required: (value) => !!value || 'Required.',
      required: (value) => {
      // ถ้าเป็น array (autocomplete multiple) ให้เช็ค length
      if (Array.isArray(value)) {
        return value.length > 0 || 'Required.'
      }
      // ปกติให้เช็ค truthy
      return !!value || 'Required.'
    },
    },
    tempAmountQuestions: 3,
    Validation: {
      validationName: null,
      vectordbName: null,
      tone: [],
      topVDB: null,
      amountQuestions: 3,
      amountQuestionsWithTone: 0,
      promptTemplate: null,
      promptMore: null,
      temperature: 0.7,
      topP: 0.7,
      frequencyPenalty: null,
      presencePenalty: null,
      maxTokens: null,
      stop: null,
      positive: true,
      firm: false,
      confident: false,
      poetic: false,
      narrative: false,
      professional: false,
      descriptive: false,
      humorous: false,
      academic: false,
      persausive: false,
      format: false,
      informal: false,
      friendly: false,
      entity: null,
    },
    DDModelShow: [],
  }),
  watch: {
    '$route.params.id': function (newParam) {
      // อัพเดตค่าพารามิเตอร์ด้วยตัวเอง
      this.id = newParam;
    },    
  },
  methods: {
    async copyToClipboard(text) {
      // ป้องกันการคลิกซ้ำ
      if (this.copyButtonState.copying) return;
      
      this.copyButtonState.copying = true;
      
      try {
        await navigator.clipboard.writeText(text);
        
        // เปลี่ยนเป็นไอคอนถูก
        this.copyButtonState.copied = true;
        this.copyButtonState.copying = false;
        setTimeout(() => {
          this.copyButtonState.copied = false;
        }, 3000);
        
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          
          // เปลี่ยนเป็นไอคอนถูก
          this.copyButtonState.copied = true;
          this.copyButtonState.copying = false;
          setTimeout(() => {
            this.copyButtonState.copied = false;
          }, 3000);
          
        } catch (fallbackErr) {
          this.copyButtonState.copying = false;
        }
        
        document.body.removeChild(textArea);
      }
    },
    toggleTone(toneType) {
      const toneMap = {
        'firm': 'Firm',
        'confident': 'Confident',
        'poetic': 'Poetic',
        'narrative': 'Narrative',
        'professional': 'Professional',
        'descriptive': 'Descriptive',
        'humorous': 'Humorous',
        'academic': 'Academic',
        'persausive': 'Persausive',
        'format': 'Format',
        'informal': 'Informal',
        'friendly': 'Friendly'
      };
      
      this.Validation[toneType] = !this.Validation[toneType];
      this.InsertTextToPrompt(this.Validation[toneType], toneMap[toneType]);
    },
    validate() {
      this.$refs.form.validate();
    },
    Get(id) {
      let self = this;
      self.dialog = true;
      self.loading = true;
      self.$call.GetValidationGenerateDetail(
        id,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.Edit(dataResponse.data);
          }
          self.dialog = false;
        },
        (errorResource) => {
          self.dialog = false;
          self.isSearch = false;
          console.error(errorResource);
        }
      );
    },
    GetDDVectorName() {
      let self = this;
      self.dialog = true;
      self.$call.GetDDVectorDBName(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.DDVector = dataResponse.data;
          } else {
            self.DDVector = [];
          }
          self.dialog = false;
        },
        (errorResource) => {
          self.dialog = false;
          console.error(errorResource);
        }
      );
    },
    Save() {
      let self = this;
      if (self.$route.query.id) {
        self.SaveEdit();
      } else {
        self.SaveAdd();
      }
    },

    SaveAdd() {
      let self = this;
      setTimeout(() => {
        self.validate();
      }, 100);
      if (
        self.Validation.validationName == null ||
        self.Validation.validationName == '' ||
        self.Validation.amountQuestions == null ||
        self.Validation.amountQuestions <= 0 ||
        self.Validation.promptTemplate == null ||
        self.Validation.promptTemplate === '' || 
        self.Validation.vectordbName === null ||
        self.Validation.vectordbName.length === 0 
      ) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: 'Please Fill All The Fields!',
          showConfirmButton: true,
          backdrop: false,
        });
        return;
      }
      self.dialog = true;
      let request = {
        name: self.Validation.validationName,
        NumberQuestions: self.Validation.amountQuestions,
        NumberQuestionsWithTone: self.Validation.amountQuestionsWithTone,
        Prompt: self.Validation.promptTemplate,
        ValidationName: self.Validation.validationName,
        VectordbName: self.Validation.vectordbName,
        Tone: self.Validation.tone,
        Temperature: self.Validation.temperature,
        TopP: self.Validation.topP,
        FrequencyPenalty: self.Validation.frequencyPenalty,
        PresencePenalty: self.Validation.presencePenalty,
        Stop: self.Validation.stop,
        MaxTokens: self.Validation.maxTokens,
        PromptMore: self.Validation.promptMore,
      };
      try {
        self.$call.CreateValidationGenerate(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
                self.$swal.fire({
                  icon: 'success',
                  title: dataResponse.message,
                  showConfirmButton: false,
                  timer: 1500,
                });
              this.$router.push('/ValidationGenerate');
              // self.Processing(dataResponse.data);
            }else {
              self.$swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: dataResponse.message,
                showConfirmButton: true,
              });
            }
            self.dialog = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, 'DeleteGroup');
      }
    },
    SaveEdit() {
      let self = this;
      setTimeout(() => {
        self.validate();
      }, 100);
      if (
        self.Validation.validationName == null ||
        self.Validation.validationName == '' ||
        self.Validation.amountQuestions == null ||
        self.Validation.amountQuestions <= 0 ||
        self.Validation.promptTemplate == null ||
        self.Validation.promptTemplate === ''
      ) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: 'Please Fill All The Fields!',
          showConfirmButton: true,
        });
        return;
      }
      self.dialog = true;
      let request = {
        GenerateID: self.$route.query.id,
        name: self.Validation.validationName,
        NumberQuestions: self.Validation.amountQuestions,
        NumberQuestionsWithTone: self.Validation.amountQuestionsWithTone,
        Prompt: self.Validation.promptTemplate,
        ValidationName: self.Validation.validationName,
        VectordbName: self.Validation.vectordbName,
        Tone: self.Validation.tone,
        Temperature: self.Validation.temperature == '' ? null : self.Validation.temperature,
        TopP: self.Validation.topP == '' ? null : self.Validation.topP,
        FrequencyPenalty: self.Validation.frequencyPenalty == '' ? null : self.Validation.frequencyPenalty,
        PresencePenalty: self.Validation.presencePenalty == '' ? null : self.Validation.presencePenalty,
        Stop: self.Validation.stop,
        MaxTokens: self.Validation.maxTokens,
        PromptMore: self.Validation.promptMore,
      };
      try {
        self.$call.EditValidationGenerate(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.$swal.fire({
                icon: 'success',
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              });
              this.$router.push('/ValidationGenerate');
              // self.Processing(dataResponse.data);
            }else if (dataResponse.status == 3) {
              self.$swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: dataResponse.message,
                showConfirmButton: true,
              });
              
            }
            self.dialog = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, 'DeleteGroup');
      }
    },
    Processing(id) {
      let self = this;
      self.dialog = true;

      let request = {
        id: id,
        entity: self.Validation.entity,
      };
      try {
        self.$call.UploadValidationGenerate(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.$swal.fire({
                icon: 'success',
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              });
              this.$router.push('/ValidationGenerate');
            }
            self.dialog = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, 'DeleteGroup');
      }
    },
    Add() {
      let self = this;
      self.Clear();
      self.Adddialog = true;
      self.DDModelShow = self.DDModel;
    },
    Edit(item) {
      let self = this;
      self.Validation.validationName = item.name;
      // self.Validation.promptMore = item.prompt
      self.Validation.vectordbName = item.vectorName
      self.Validation.amountQuestions = item.numberQuestions

      self.Validation.temperature = item.temperature;
      self.Validation.topP = item.topP;
      self.Validation.frequencyPenalty = item.frequencyPenalty;
      self.Validation.presencePenalty = item.presencePenalty;
      self.Validation.stop = item.stop;
      self.Validation.maxTokens = item.maxTokens;
      self.Validation.promptMore = item.promptMore;

      self.Validation.amountQuestionsWithTone = item.numberQuestionsWithTone;
      self.Validation.tone = item.tone == null ? [] : item.tone;
      self.setToneAll(item.tone);

    },
    setToneAll(tone) {
      let self = this;
      if (tone != null) {
        self.Validation.firm = tone.includes('Firm');
        self.Validation.confident = tone.includes('Confident');
        self.Validation.poetic = tone.includes('Poetic');
        self.Validation.narrative = tone.includes('Narrative');
        self.Validation.professional = tone.includes('Professional');
        self.Validation.descriptive = tone.includes('Descriptive');
        self.Validation.humorous = tone.includes('Humorous');
        self.Validation.academic = tone.includes('Academic');
        self.Validation.persausive = tone.includes('Persausive');
        self.Validation.format = tone.includes('Format');
        self.Validation.informal = tone.includes('Informal');
        self.Validation.friendly = tone.includes('Friendly');
      }

    },

    Clear() {
      let self = this;
      self.Validation.firm = false;
      self.Validation.confident = false;
      self.Validation.poetic = false;
      self.Validation.narrative = false;
      self.Validation.professional = false;
      self.Validation.descriptive = false;
      self.Validation.humorous = false;
      self.Validation.academic = false;
      self.Validation.persausive = false;
      self.Validation.format = false;
      self.Validation.informal = false;
      self.Validation.friendly = false;
    },
    chkTone() {
      let self = this;
      return !(
        self.Validation.firm ||
        self.Validation.confident ||
        self.Validation.poetic ||
        self.Validation.narrative ||
        self.Validation.professional ||
        self.Validation.descriptive ||
        self.Validation.humorous ||
        self.Validation.academic ||
        self.Validation.persausive ||
        self.Validation.format ||
        self.Validation.informal ||
        self.Validation.friendly
      );
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }

      this.today = dd + '/' + mm + '/' + yyyy;
    },
    isNumber: function (evt) {
      evt = evt ? evt : window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46 && charCode !== 45) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    isNumberInt: function (evt) {
      evt = evt ? evt : window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    validateInput0_1(inputValue) {
      // ตรวจสอบว่าข้อมูลที่ป้อนเข้ามาเป็นตัวเลขที่อยู่ระหว่าง 0 ถึง 1 ที่มีทศนิยม 2 ตำแหน่ง
      const value = parseFloat(inputValue);
      if (isNaN(value)) return 'Required.';
      return (value >= 0 && value <= 1 && this.isTwoDecimalPlaces(value)) || 'กรุณาป้อนตัวเลขระหว่าง 0 ถึง 1 ที่มีทศนิยม 2 ตำแหน่ง';
    },
    validateInput0_2(inputValue) {
      // ตรวจสอบว่าข้อมูลที่ป้อนเข้ามาเป็นตัวเลขที่อยู่ระหว่าง 0 ถึง 2 ที่มีทศนิยม 2 ตำแหน่ง
      const value = parseFloat(inputValue);
      if (isNaN(value)) return true;
      return (value >= 0 && value <= 2 && this.isTwoDecimalPlaces(value)) || 'กรุณาป้อนตัวเลขระหว่าง 0 ถึง 2 ที่มีทศนิยม 2 ตำแหน่ง';
    },
    isTwoDecimalPlaces(number) {
      const regex = /^\d+(\.\d{1,2})?$/;
      return regex.test(number.toString());
    },
    isIntPlaces(number) {
      const regex = /^\d+(\d{1,2})?$/;
      return regex.test(number.toString());
    },
    InsertTextToPrompt(flag, text) {
      let self = this;
      if (flag) {
        self.Validation.tone.push(text);
      } else {
        const index = self.Validation.tone.indexOf(text);
        if (index !== -1) {
          self.Validation.tone.splice(index, 1);
        }
      }
    },
    InsertNumberQuestionToPrompt(number) {
      let self = this;
      let template = 'You will need to generate ';
      self.tempAmountQuestions = self.Validation.amountQuestions;
      if (self.Validation.amountQuestions && number) {
        self.tempAmountQuestions = self.Validation.amountQuestions;
        self.Validation.promptTemplate = self.Validation.promptTemplate.replace(template + number, template + self.Validation.amountQuestions);
      } else if (number) {
        self.Validation.promptTemplate = self.Validation.promptTemplate.replace(
          `\n- ${template}${number} questions with meaningful and relevant questions with original question from users only.`,
          ''
        );
      } else {
        self.Validation.promptTemplate += `\n- You will need to generate ${self.Validation.amountQuestions} questions with meaningful and relevant questions with original question from users only.`;
      }
    },
    processUpload(e) {
      this.files = e;
      if (this.files.length == 0) {
        this.Validation.entity = null;
        this.uploadedFiles = [];
        return;
      }
      const reader = new FileReader();
      reader.readAsText(this.files[0]);
      reader.onload = () => {
        let text = reader.result;
        this.ValidateCSVVector(text, this.files);
      };
    },
    ValidateCSVVector(csv, files) {
      let temp = csv.replaceAll('\r', '');
      let lines = temp.split('\n');
      if (lines[0] != 'Questions') {
        this.Validation.entity = null;
        this.uploadedFiles = [];
        alert('Please Enter Correct Format!');
      } else {
        const formDataDoc = new FormData();
        formDataDoc.append('entity', files[0]);
        this.uploadedFiles = files;
        this.Validation.entity = formDataDoc;
      }
    },
    removeFile(fileName) {
      // Find the index of the
      const index = this.uploadedFiles.findIndex((file) => file.name === fileName);
      // If file is in uploaded files remove it
      if (index > -1) this.uploadedFiles.splice(index, 1);
      this.$emit('filesUploaded', this.uploadedFiles);
    },
    download() {
      const data = [];
      data.push('\ufeffQuestions\n');
      data.push('จังหวัดที่อยู่เหนือสุดของประเทศไทย\n');
      data.push('จังหวัดที่อยู่ใต้สุดของประเทศไทย\n');
      data.push('จังหวัดที่มีพื้นที่มากที่สุดของประเทศไทย');

      const a = document.createElement('a');
      let blob = new Blob(data, { type: 'data:text/csv;charset=UTF-8' });
      const url = window.URL.createObjectURL(blob);

      a.href = url;
      a.download = 'upload_questions.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    },
  },
  mounted() {
    let self = this;
    self.GetDate();
    self.GetDDVectorName();
    if (self.$route.query.id) {
      self.Get(self.$route.query.id);
    }
    // self.Validation.promptTemplate =
    //   "You're assistant for generate questions from original question of users.\n#Condition\n- Your main task is to generate questions from original question of users.\n- You must know that the original question from user is not a question, you don't have to answer those questions. Your duty is to generate questions from original question of users only.\n###NUNBEROFQUESTIONS###\n###TONE###\n#Context";
    // self.Validation.promptTemplate =  "You're assistant for generate questions from original question of users.\n#Condition\n- Your main task is to generate questions from original question of users.\n- You must know that the original question from user is not a question, you don't have to answer those questions. Your duty is to generate questions from original question of users only.\n- If in the #Context consist of many topics. such as,\n1. Topic1, 2. Topic2, 3. Topic3. You will must summarize all topics into just one topic only.\n###NUNBEROFQUESTIONS###\n- You don't need to show the summary.\n###TONE###\n#Context";

    self.Validation.promptTemplate =
      "You're assistant for generate questions from original question of users.\n#Condition\n- Your main task is to generate questions from original context of users.\n- Your duty is to generate questions from original question of users only.\n- The question that you generate need to response same as context language.\n- If in the #Context consist of sub-topics you need to summarize all context first before doing generate question.\n###NUNBEROFQUESTIONS###\n- You don't need to show the summary.\n###TONE###\n###PromptMore###\n#Context";
  },
};
</script>
<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
}

textarea {
  min-height: 800px !important;
}

.flex {
  margin-left: 2rem;
  margin-right: 2rem;
}

.icon {
  order: 0;
}

.header {
  order: 1;
  font-weight: 700;
}

.v-text-field ::v-deep label {
  font-size: 0.9375rem;
}

.v-expansion-panel-header {
  padding: 0;
}


.main-form-card {
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08) !important;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%) !important;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

.form-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 2rem;
}

.form-left-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-right-panel {
  display: flex;
  flex-direction: column;
}

.form-section {
  margin-bottom: 1rem;
}

.prompt-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.modern-expansion-panels {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.expansion-panel-modern {
  background: white !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.expansion-panel-modern:last-child {
  border-bottom: none !important;
}

.panel-header {
  padding: 1.25rem 1.5rem !important;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.panel-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.panel-content {
  padding: 1.5rem !important;
  background: white !important;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.tone-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.tone-checkbox {
  background: rgba(59, 130, 246, 0.05) !important;
  border: 1px solid rgba(59, 130, 246, 0.1) !important;
  border-radius: 8px !important;
  padding: 0.75rem !important;
  transition: all 0.2s ease !important;
}

.tone-checkbox:hover {
  background: rgba(59, 130, 246, 0.08) !important;
  border-color: rgba(59, 130, 246, 0.2) !important;
}

.checkbox-label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.modern-input :deep(.v-field) {
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.modern-input :deep(.v-field:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.modern-autocomplete :deep(.v-field) {
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.modern-autocomplete :deep(.v-field:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.modern-textarea :deep(.v-field) {
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.modern-textarea :deep(.v-field:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  gap: 2rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .form-layout {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .form-grid-2 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .tone-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .form-actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }
}

/* Animation Enhancements */
.expansion-panel-modern {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.tone-checkbox {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modern-input, .modern-autocomplete, .modern-textarea {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.tone-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.tone-option-card {
  border: 2px solid rgba(59, 130, 246, 0.1) !important;
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;
  background: white !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.tone-option-card:hover {
  border-color: rgba(59, 130, 246, 0.3) !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15) !important;
  transform: translateY(-2px) !important;
}

.tone-option-card.selected {
  border-color: rgb(59, 130, 246) !important;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%) !important;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2) !important;
}

.tone-card-content {
  padding: 1rem !important;
}

.tone-checkbox-minimal {
  min-width: auto !important;
  margin: 0 !important;
  padding: 0 !important;
}

.tone-checkbox-minimal :deep(.v-input__control) {
  min-height: auto !important;
}

.tone-content {
  flex: 1;
  min-width: 0;
}

.tone-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.tone-description {
  font-size: 0.78rem;
  font-weight: 400;
  color: #0000006e;
  margin: 0;
  line-height: 1;
  word-wrap: break-word;
  
}

.tone-option-card.selected .tone-title {
  color: rgb(59, 130, 246);
}

.tone-option-card.selected .tone-description {
  color: #4b5563;
}

/* Icon และ Primary color updates */
.section-title .v-icon,
.panel-header .v-icon {
  color: rgb(59, 130, 246) !important;
}

.modern-input :deep(.v-field--focused) {
  border-color: rgb(59, 130, 246) !important;
}

.modern-autocomplete :deep(.v-field--focused) {
  border-color: rgb(59, 130, 246) !important;
}

.modern-textarea :deep(.v-field--focused) {
  border-color: rgb(59, 130, 246) !important;
}

/* Checkbox color updates */
.tone-checkbox-minimal :deep(.v-selection-control__input .v-icon) {
  color: rgb(59, 130, 246) !important;
}

/* Tooltip และ info icon colors */
.modern-input :deep(.v-icon),
.modern-autocomplete :deep(.v-icon),
.modern-textarea :deep(.v-icon) {
  color: rgb(59, 130, 246) !important;
}

/* Button สี primary */
.create-btn {
  background: linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(37, 99, 235) 100%) !important;
  color: white !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}

.create-btn:hover {
  background: linear-gradient(135deg, rgb(37, 99, 235) 0%, rgb(29, 78, 216) 100%) !important;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4) !important;
}

.create-btn:disabled {
  background: rgba(59, 130, 246, 0.5) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  box-shadow: none !important;
}

/* Chip colors for autocomplete */
.modern-autocomplete :deep(.v-chip) {
  background: rgba(59, 130, 246, 0.1) !important;
  color: rgb(59, 130, 246) !important;
  border: 1px solid rgba(59, 130, 246, 0.2) !important;
}

.modern-autocomplete :deep(.v-chip:hover) {
  background: rgba(59, 130, 246, 0.15) !important;
}

/* Loading และ progress colors */
.loading-card .v-progress-circular {
  color: rgb(59, 130, 246) !important;
}

/* Expansion panel selected state */
.expansion-panel-modern :deep(.v-expansion-panel-title--active) {
  color: rgb(59, 130, 246) !important;
}

/* Responsive adjustments for tone cards */
@media (max-width: 768px) {
  .tone-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .tone-card-content {
    padding: 0.875rem !important;
  }
  
  .tone-title {
    font-size: 0.9375rem;
  }
  
  .tone-description {
    font-size: 0.8125rem;
  }
}

/* Animation for smooth state changes */
.tone-option-card {
  position: relative;
  overflow: hidden;
}

.tone-option-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
  transition: left 0.6s ease;
}

.tone-option-card:hover::before {
  left: 100%;
}

/* Form field focus states */
.form-section :deep(.v-field--focused .v-field__outline) {
  --v-field-border-color: rgb(59, 130, 246) !important;
}

.form-section :deep(.v-label--active) {
  color: rgb(59, 130, 246) !important;
}

/* Page header icon */
.page-header .v-icon {
  color: rgb(59, 130, 246) !important;
}

/* Back button outline color */
.back-button[variant="outlined"] {
  border-color: rgb(249, 115, 22) !important;
  color: rgb(249, 115, 22) !important;
}

.back-button:hover {
  background: rgba(249, 115, 22, 0.1) !important;
}

.readonly-textarea :deep(.v-field) {
  background-color: #f8f9fa !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.readonly-textarea :deep(.v-field__input) {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  cursor: default !important;
}

.readonly-textarea :deep(.v-field--focused) {
  border-color: rgba(59, 130, 246, 0.3) !important;
}

.readonly-textarea :deep(.v-field__overlay) {
  background-color: transparent !important;
}

.readonly-textarea :deep(.v-label) {
  color: #6c757d !important;
}

.readonly-textarea :deep(.v-field--focused .v-label) {
  color: rgb(59, 130, 246) !important;
}

/* Ensure text is still readable */
.readonly-textarea :deep(textarea) {
  color: #495057 !important;
  background-color: transparent !important;
  cursor: default !important;
}

.readonly-textarea :deep(textarea::selection) {
  background-color: rgba(59, 130, 246, 0.2) !important;
}

/* Icon styling for readonly */
.readonly-textarea :deep(.v-icon) {
  color: rgb(59, 130, 246) !important;
}


.copy-btn {
  opacity: 0.7;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  opacity: 1;
  background-color: rgba(59, 130, 246, 0.1) !important;
}

.copy-btn:disabled {
  opacity: 0.5 !important;
  pointer-events: none;
}

/* Success state for copy button */
.copy-btn.v-btn--color-success {
  background-color: rgba(34, 197, 94, 0.1) !important;
}

.copy-btn.v-btn--color-success:hover {
  background-color: rgba(34, 197, 94, 0.15) !important;
}

/* Icon animation */
.copy-btn :deep(.v-icon) {
  transition: all 0.3s ease;
}

.copy-btn.v-btn--color-success :deep(.v-icon) {
  color: rgb(34, 197, 94) !important;
  animation: checkmark-bounce 0.4s ease-in-out;
}

/* Checkmark bounce animation */
@keyframes checkmark-bounce {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Copying state */
.copy-btn:disabled :deep(.v-icon) {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

.readonly-textarea:hover .copy-btn {
  opacity: 0.9;
}

/* Icon container spacing */
.readonly-textarea :deep(.v-field__append-inner) {
  padding-left: 0.5rem;
}

/* Ensure proper alignment */
.d-flex.align-center.gap-1 > * {
  flex-shrink: 0;
}
</style>
